// Per-page head tags for TanStack Router's `head` option.
//
// Why this exists: page components used react-helmet-async, which does not
// server-render in this build, so every page shipped the root route's default
// title and description to crawlers. Route-level `head` does render (it is how
// __root.tsx sets the defaults), so page metadata lives here instead.

export const SITE_URL = "https://kodiakse.com";
export const SITE_NAME = "Kodiak Structures & Exteriors";

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface SeoInput {
  title: string;
  description?: string | undefined;
  /** Site-root-relative path, e.g. "/services/siding". Drives canonical + og:url. */
  path: string;
  image?: string | null | undefined;
  /** Utility pages (admin, confirmations) that should stay out of the index. */
  noindex?: boolean | undefined;
  type?: ("website" | "article") | undefined;
}

export function seo({
  title,
  description,
  path,
  image,
  noindex,
  type = "website",
}: SeoInput) {
  const url = `${SITE_URL}${path === "/" ? "" : path.replace(/\/+$/, "")}`;
  const meta: Array<Record<string, string>> = [
    { title },
    { property: "og:title", content: title },
    { name: "twitter:title", content: title },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
  ];

  if (description) {
    meta.push(
      { name: "description", content: description },
      { property: "og:description", content: description },
      { name: "twitter:description", content: description },
    );
  }

  // Bundled assets resolve to root-relative paths; og:image must be absolute.
  const rawImage = image || DEFAULT_OG_IMAGE;
  const ogImage = rawImage.startsWith("/") ? `${SITE_URL}${rawImage}` : rawImage;
  meta.push(
    { property: "og:image", content: ogImage },
    { name: "twitter:image", content: ogImage },
  );

  if (noindex) meta.push({ name: "robots", content: "noindex, nofollow" });

  return {
    meta,
    links: noindex ? [] : [{ rel: "canonical", href: url }],
  };
}

/** Collapse HTML/markdown to a clean meta description. */
export function toDescription(input: string, max = 155): string {
  const text = input
    .replace(/<[^>]+>/g, " ")
    .replace(/[#*_>`]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max).replace(/\s+\S*$/, "")}...`;
}
